import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';

import { WordpressContentService } from '../../aboutUs/wordpress-content.service';

type NewsMediaContentState = 'loading' | 'error' | 'empty' | 'content';

interface ContentResult {
  content: string;
  state: Exclude<NewsMediaContentState, 'loading'>;
}

const DAP_WORDPRESS_ORIGIN = 'https://disasteraccountability.org';
const NON_PAGE_EXTENSIONS = new Set([
  '7z', 'apk', 'avif', 'bin', 'bmp', 'css', 'csv', 'dmg', 'doc', 'docx', 'eot', 'exe', 'gif', 'gz', 'ico', 'jpeg',
  'jpg', 'js', 'json', 'mov', 'mp3', 'mp4', 'mpeg', 'msi', 'ogg', 'pdf', 'png', 'ppt', 'pptx', 'rar', 'rtf',
  'svg', 'tar', 'tif', 'tiff', 'ttf', 'txt', 'wav', 'webm', 'webmanifest', 'webp', 'woff', 'woff2', 'xls', 'xlsx',
  'xml', 'zip',
]);
const NON_PAGE_ROOTS = new Set([
  'api', 'assets', 'author', 'downloads', 'files', 'smart-response', 'wp-admin', 'wp-content', 'wp-includes',
  'wp-json',
]);
const NON_PAGE_SEGMENTS = new Set(['uploads', 'wp-content', 'wp-includes']);

@Component({
  selector: 'news-media-content',
  templateUrl: './news-media-content.component.html',
  styleUrls: ['./news-media-content.component.css'],
})
export class NewsMediaContentComponent implements OnDestroy, OnInit {
  state: NewsMediaContentState = 'loading';
  content = '';
  isListContent = true;

  private canonicalPath = '';
  private contentSubscription?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private wordpressContentService: WordpressContentService
  ) {}

  ngOnInit(): void {
    const effectivePath$: Observable<string> = combineLatest([this.route.data, this.route.queryParamMap]).pipe(
      map(([data, queryParamMap]) => this.getEffectivePath(data.canonicalPath as string, queryParamMap)),
      distinctUntilChanged(),
      tap((path) => {
        this.content = '';
        this.state = 'loading';
        this.isListContent = this.isCanonicalListPath(path);
      })
    );

    this.contentSubscription = effectivePath$
      .pipe(
        switchMap((path) =>
          this.wordpressContentService.getContent(path).pipe(
            map((response): ContentResult => {
              if (!response.content || !response.content.trim()) {
                return { content: '', state: 'empty' };
              }

              return {
                content: this.normalizeWordpressContent(response.content, this.isListContent),
                state: 'content',
              };
            }),
            catchError((): Observable<ContentResult> => of({ content: '', state: 'error' }))
          )
        )
      )
      .subscribe((result) => {
        this.content = result.content;
        this.state = result.state;
      });
  }

  ngOnDestroy(): void {
    this.contentSubscription?.unsubscribe();
  }

  onWordpressClick(event: MouseEvent): void {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      event.altKey ||
      !(event.target instanceof Element)
    ) {
      return;
    }

    const anchor = event.target.closest('a[href]') as HTMLAnchorElement | null;
    if (
      !anchor ||
      !(event.currentTarget instanceof Element) ||
      !event.currentTarget.contains(anchor) ||
      anchor.hasAttribute('target') ||
      anchor.hasAttribute('download')
    ) {
      return;
    }

    const href = anchor.getAttribute('href');
    if (
      !href ||
      href !== href.trim() ||
      href.startsWith('#') ||
      href.includes('\\') ||
      /[\u0000-\u001f\u007f]/.test(href) ||
      this.hasDotSegment(href)
    ) {
      return;
    }

    let url: URL;
    try {
      url = new URL(href, DAP_WORDPRESS_ORIGIN + '/');
    } catch (_error) {
      return;
    }

    if (url.origin !== DAP_WORDPRESS_ORIGIN || url.hash || url.search) {
      return;
    }

    const path = this.validatePathUrl(url.pathname);
    if (!path) {
      return;
    }

    event.preventDefault();
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { pathUrl: path === this.canonicalPath ? null : path },
      queryParamsHandling: 'merge',
    });
  }

  private getEffectivePath(canonicalPath: string, queryParamMap: ParamMap): string {
    this.canonicalPath = canonicalPath;
    const rawPath = queryParamMap.get('pathUrl');
    if (rawPath === null) {
      return canonicalPath;
    }

    const validatedPath = this.validatePathUrl(rawPath);
    if (!validatedPath || validatedPath === canonicalPath) {
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { pathUrl: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
      return canonicalPath;
    }

    return validatedPath;
  }

  private validatePathUrl(value: string): string | null {
    if (
      !value ||
      value.length > 2048 ||
      value !== value.trim() ||
      value.startsWith('//') ||
      value.includes('\\') ||
      value.includes('?') ||
      value.includes('#') ||
      /[\u0000-\u001f\u007f]/.test(value)
    ) {
      return null;
    }

    const path = value.replace(/^\/+|\/+$/g, '');
    const segments = path.split('/');
    if (!path || segments.some((segment) => !segment)) {
      return null;
    }

    const decodedSegments: string[] = [];
    for (const segment of segments) {
      let decodedSegment = segment;
      try {
        for (let decodeCount = 0; decodeCount < 3; decodeCount += 1) {
          const nextValue = decodeURIComponent(decodedSegment);
          if (nextValue === decodedSegment) {
            break;
          }
          decodedSegment = nextValue;
        }
      } catch (_error) {
        return null;
      }

      if (
        !decodedSegment ||
        decodedSegment === '.' ||
        decodedSegment === '..' ||
        decodedSegment.startsWith('.') ||
        decodedSegment.includes('/') ||
        decodedSegment.includes('\\') ||
        /[\s<>:"#?*|]/.test(decodedSegment) ||
        /[\u0000-\u001f\u007f]/.test(decodedSegment)
      ) {
        return null;
      }
      decodedSegments.push(decodedSegment);
    }

    const lowerCaseSegments = decodedSegments.map((segment) => segment.toLowerCase());
    const finalSegmentParts = lowerCaseSegments.slice(-1)[0].split('.');
    const extension = finalSegmentParts.length > 1 ? finalSegmentParts.slice(-1)[0] : '';
    if (
      NON_PAGE_ROOTS.has(lowerCaseSegments[0]) ||
      lowerCaseSegments.some((segment) => NON_PAGE_SEGMENTS.has(segment)) ||
      NON_PAGE_EXTENSIONS.has(extension)
    ) {
      return null;
    }

    return path;
  }

  private isCanonicalListPath(path: string): boolean {
    if (path === this.canonicalPath) {
      return true;
    }

    const paginationPath = path.slice(this.canonicalPath.length + 1);
    return path.startsWith(this.canonicalPath + '/') && /^page\/[1-9]\d*$/.test(paginationPath);
  }

  private hasDotSegment(value: string): boolean {
    let decodedValue = value;
    try {
      for (let decodeCount = 0; decodeCount < 3; decodeCount += 1) {
        const nextValue = decodeURIComponent(decodedValue);
        if (nextValue === decodedValue) {
          break;
        }
        decodedValue = nextValue;
      }
    } catch (_error) {
      return true;
    }

    return /(?:^|\/)\.{1,2}(?:\/|$|[?#])/.test(decodedValue);
  }

  private normalizeWordpressContent(content: string, isListContent: boolean): string {
    const documentNode = new DOMParser().parseFromString(content, 'text/html');
    const headings = documentNode.querySelectorAll('h1');

    headings.forEach((heading) => {
      const replacement = documentNode.createElement('h2');
      Array.from(heading.attributes).forEach((attribute) => replacement.setAttribute(attribute.name, attribute.value));
      while (heading.firstChild) {
        replacement.appendChild(heading.firstChild);
      }
      heading.replaceWith(replacement);
    });

    const authorAnchors = documentNode.querySelectorAll('.metadata-posted-on a.url.fn.n');
    authorAnchors.forEach((anchor) => this.replaceAnchorWithSpan(documentNode, anchor));

    if (!isListContent) {
      const metadataAnchors = documentNode.querySelectorAll('.metadata-posted-on a');
      metadataAnchors.forEach((anchor) => {
        if (anchor.matches('.entry-date') || anchor.querySelector('.entry-date')) {
          this.replaceAnchorWithSpan(documentNode, anchor);
        }
      });
    }

    this.normalizeTableCellSpacing(documentNode);

    return documentNode.body.innerHTML;
  }

  private normalizeTableCellSpacing(documentNode: Document): void {
    documentNode.querySelectorAll('td, th').forEach((cell) => {
      const textNodes = documentNode.createTreeWalker(cell, NodeFilter.SHOW_TEXT);
      let textNode = textNodes.nextNode();
      while (textNode) {
        textNode.nodeValue = textNode.nodeValue?.replace(/\u00a0{2,}/g, ' ') ?? null;
        textNode = textNodes.nextNode();
      }
    });
  }

  private replaceAnchorWithSpan(documentNode: Document, anchor: Element): void {
    const replacement = documentNode.createElement('span');
    if (anchor.className) {
      replacement.className = anchor.className;
    }
    while (anchor.firstChild) {
      replacement.appendChild(anchor.firstChild);
    }
    anchor.replaceWith(replacement);
  }
}
